import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsGeneral';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import api from '../../../../../api';
import Modal from './AreaNegocioModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { isSessionSuperAdmin, getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Área de Negocio";
const endpointRead = (tipo) => ENDPOINTS.areaNegocioSecurity(tipo);
const endpointWrite = ENDPOINTS.areasNegocios;
const endpointById = ENDPOINTS.areasNegociosById;
const modelo = Models.areasNegocios;

const maps = {
  mapCreate: (f) => ({
    clave: f.clave,
    nombre: f.nombre,
    descripcion: f.descripcion,
    activo: !!f.activo,
  }),
  mapUpdate: (id, f) => ({
    idAreaNegocio: id,
    clave: f.clave,
    nombre: f.nombre,
    descripcion: f.descripcion,
    activo: !!f.activo,
  }),
};

export default function Catalog() {
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });
  const [showChildData, setShowChildData] = useState(false);


  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get(endpointRead(showChildData ? 'hijas' : 'propias'));
      setRows((res.data || []).map((x) => modelo(x)));
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al cargar información. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [showChildData]);

  const filtered = useMemo(() => {
    const isSuperAdmin = isSessionSuperAdmin();
    const sessionEmpresaId = getSessionEmpresaId();

    let data = rows; // Filtrado por backend

    // 2. Filtro de búsqueda
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      data = data.filter((r) => {
        const clave = (r.clave || '').toLowerCase();
        const nombre = (r.nombre || '').toLowerCase();
        const descripcion = (r.descripcion || '').toLowerCase();
        const empresa = (r.nombreEmpresa || '').toLowerCase();
        return clave.includes(q) || nombre.includes(q) || descripcion.includes(q) || empresa.includes(q);
      });
    }
    return data;
  }, [rows, query, showChildData]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idAreaNegocio === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await api.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || 'Ocurrió un error al eliminar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await api.post(endpointWrite, maps.mapCreate(payload));
        const newId = res?.data?.idAreaNegocio ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await api.put(endpointWrite, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
      triggerAlert(msg);
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por clave, nombre o descripción…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />



          {!isSessionSuperAdmin() && (
            <button 
              className={`btn-filter ${showChildData ? 'active' : ''}`} 
              onClick={() => { setShowChildData(!showChildData); setPage(1); }}
            >
              {showChildData ? '👁️ Ver Propios' : '🌿 Ver Hijas'}
            </button>
          )}

          {puedeCrear && (
            <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
          )}
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Clave</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const sessionEmpresaId = getSessionEmpresaId();
                  const isOwn = isSessionSuperAdmin() || r.idEmpresa === sessionEmpresaId || !r.idEmpresa;

                  const isLockedByHierarchy = !isOwn && r.tieneEmpleados;
                  const isLockedByEmployees = r.tieneEmpleados;

                  const hierarchyTitle = "Bloqueado: Empresa hija con empleados";
                  const employeesTitle = "Bloqueado: Esta área tiene empleados asignados activos.";

                  return (
                    <tr key={r.idAreaNegocio} className={editingRowId === r.idAreaNegocio ? 'rk-row-active' : ''}>
                      <td><strong>{normalizeValue(r.clave)}</strong></td>
                      <td>{normalizeValue(r.nombre)}</td>
                      <td>{normalizeValue(r.descripcion)}</td>
                      <td>
                        <span style={{ fontSize: '0.85rem', color: r.idEmpresa ? '#334155' : '#94a3b8' }}>
                          {r.nombreEmpresa || 'Global'}
                        </span>
                      </td>
                      {puedeEditar && (
                        <td style={{ textAlign: 'center' }}>
                          {isLockedByHierarchy ? (
                            <span title={hierarchyTitle} style={{ cursor: 'not-allowed', opacity: 0.5 }}>
                              🔒
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="rk-iconbtn rk-iconbtn--edit"
                              onClick={() => handleEdit(r.idAreaNegocio)}
                              aria-label="Editar"
                              title="Editar"
                            >
                              <img src={sigmaEdit} alt="" className="rk-ico" />
                            </button>
                          )}
                        </td>
                      )}
                      {puedeEliminar && (
                        <td style={{ textAlign: 'center' }}>
                          {isLockedByHierarchy ? (
                            <span title={hierarchyTitle} style={{ cursor: 'not-allowed', opacity: 0.5 }}>
                              🔒
                            </span>
                          ) : isLockedByEmployees ? (
                            <span title={employeesTitle} style={{ cursor: 'not-allowed', opacity: 0.3, display: 'inline-block' }}>
                              <img src={sigmaDelete} alt="Bloqueado" className="rk-ico" style={{ filter: 'grayscale(100%)' }} />
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="rk-iconbtn rk-iconbtn--del"
                              onClick={() => {
                                setEditingRowId(null);
                                const name = r.nombre || 'Área de Negocio';
                                askDelete(r.idAreaNegocio, name);
                              }}
                              aria-label="Borrar"
                              title="Borrar"
                            >
                              <img src={sigmaDelete} alt="" className="rk-ico" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={4 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
